import type { PillTone } from '../components/Pill/Pill';
import { PP_DEFAULT_MOCK_PATIENT_ID } from '../patients/config';
import { resolvePatientMockBundleKey } from '../patients/utils';
import { PP_INSURANCE_V2_BY_PATIENT } from './mockDataV2';
import type {
  PiV2Appointment,
  PiV2Bundle,
  PiV2Claim,
  PiV2CoverageState,
  PiV2Eligibility,
  PiV2Opportunity,
  PiV2PlanRole,
  PiV2PreauthStatus,
  PiV2Tier,
} from './typesV2';

/**
 * The band only renders for an appointment inside this window. Opportunities
 * are things to say to a person who is about to be in the chair; a visit five
 * weeks out is not a reason to raise anything today, and the estimate would be
 * quoted against benefits that can still change.
 */
export const PI_V2_VISIT_WINDOW_DAYS = 7;

/** Whole days from today to the appointment. Negative = in the past. */
export function piV2DaysUntil(iso: string): number {
  const target = new Date(iso);
  if (Number.isNaN(target.getTime())) return Number.POSITIVE_INFINITY;
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  return Math.round((startOfDay(target) - startOfDay(new Date())) / 86400000);
}

export function piV2IsInVisitWindow(appointment: PiV2Appointment): boolean {
  const days = piV2DaysUntil(appointment.at);
  return days >= 0 && days <= PI_V2_VISIT_WINDOW_DAYS;
}

/** "today" / "tomorrow" / "in 4 days" — the framing the desk uses out loud. */
export function piV2RelativeDay(iso: string): string {
  const days = piV2DaysUntil(iso);
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days < 0) return 'in the past';
  return `in ${String(days)} days`;
}

export function piV2Money(n: number | null | undefined): string {
  if (n == null) return '—';
  return `$${n.toLocaleString()}`;
}

export const PI_V2_ROLE_LABEL: Record<PiV2PlanRole, string> = {
  primary: 'Primary',
  secondary: 'Secondary',
  tertiary: 'Tertiary',
};

/**
 * T0–T4 from the opportunity engine, rendered as the design's badges.
 * T0/T1 are the ones a person must not walk past; T4 is suppressible and
 * should not have reached the page at all.
 */
export function piV2TierBadge(tier: PiV2Tier): { label: string; tone: PillTone } {
  if (tier === 'T0' || tier === 'T1') return { label: 'High', tone: 'gold' };
  if (tier === 'T2') return { label: 'Medium', tone: 'info' };
  return { label: 'Low', tone: 'neutral' };
}

const TIER_ORDER: Record<PiV2Tier, number> = { T0: 0, T1: 1, T2: 2, T3: 3, T4: 4 };

/** Tier first, then money — the top row is the one worth raising. */
export function piV2SortOpportunities(items: PiV2Opportunity[]): PiV2Opportunity[] {
  return [...items].sort((a, b) => {
    const byTier = TIER_ORDER[a.tier] - TIER_ORDER[b.tier];
    if (byTier !== 0) return byTier;
    return (b.money ?? 0) - (a.money ?? 0);
  });
}

export function piV2OpportunityMoney(o: PiV2Opportunity): string {
  if (o.moneyText != null && o.moneyText !== '') return o.moneyText;
  return piV2Money(o.money);
}

export function piV2CoverageTone(state: PiV2CoverageState): PillTone {
  if (state === 'covered') return 'success';
  if (state === 'not_covered') return 'warning';
  return 'neutral';
}

/** `unknown` says so. It is never rendered as a denial. */
export function piV2CoverageLabel(state: PiV2CoverageState, percent?: string): string {
  if (state === 'covered') return percent ?? 'Covered';
  if (state === 'not_covered') return 'Not covered';
  return 'Unknown';
}

export function piV2PreauthTone(status: PiV2PreauthStatus): PillTone {
  if (status === 'approved') return 'success';
  if (status === 'pending') return 'gold';
  if (status === 'denied') return 'warning';
  return 'neutral';
}

export const PI_V2_PREAUTH_LABEL: Record<PiV2PreauthStatus, string> = {
  approved: 'Approved',
  pending: 'Pending',
  denied: 'Denied',
  not_required: 'Not required',
};

export function piV2ClaimTone(status: PiV2Claim['status']): PillTone {
  if (status === 'paid') return 'success';
  if (status === 'pending') return 'gold';
  return 'warning';
}

/**
 * Eligibility as a sentence. `ok` states the date as provenance — no "2 days
 * ago", no staleness colour, because Core Insurance re-checks 24h before each
 * appointment and freshness is handled there.
 */
export function piV2EligibilityText(e: PiV2Eligibility): { label: string; tone: PillTone } {
  if (e.state === 'ok') {
    return { label: e.checkedAt != null ? `Verified ${e.checkedAt}` : 'Verified', tone: 'success' };
  }
  if (e.state === 'failed') {
    return {
      label:
        e.reason != null && e.reason !== ''
          ? `Verification failed — ${e.reason}`
          : 'Verification failed',
      tone: 'warning',
    };
  }
  return { label: 'Not verified', tone: 'neutral' };
}

export function piV2Pct(part: number, whole: number): number {
  if (whole <= 0) return 0;
  return Math.min((part / whole) * 100, 100);
}

export function getPatientInsuranceV2Bundle(patientId: string): PiV2Bundle {
  const key = resolvePatientMockBundleKey(patientId);
  const fallback = PP_INSURANCE_V2_BY_PATIENT[PP_DEFAULT_MOCK_PATIENT_ID];
  const bundle = PP_INSURANCE_V2_BY_PATIENT[key] ?? fallback;
  return bundle ?? { plans: [], treatmentPlan: { total: 0, insurance: 0, patient: 0, rows: [] }, claims: [] };
}
